/**
 * The cover POOL — the backend's own library of plan cover images, filtered to one category.
 *
 * ── Why this appears only now ────────────────────────────────────────────────────────────────
 * Covers moved to a server-side pool on 15 August 2026: a plan carries a `coverId` into it and the
 * backend answers with a resolved `coverImageUrl`. Until today nothing in this app read the pool,
 * so `pages/plan-builder/model/planMetaView.ts` DISPLAYS the resolved image and says in a sentence
 * that the choice is unavailable, and `model/planCover.ts`'s bundled manifest is the fallback for
 * a plan whose `coverId` is null. Both of those stay exactly as they are.
 *
 * What changed is that the 21.09 mockup draws a cover row in „Nowy program", so there is now a
 * surface that offers the pool. This is its read, and it is the entity's rather than the feature's
 * because the thing being listed is a property of a plan draft — the second consumer will be the
 * builder's own picker when it is built, and the key must be the same one then.
 *
 * ── Keyed on the category, because the request is ────────────────────────────────────────────
 * `GET /plan-covers?category=` narrows server-side, so two categories are two cache entries. Not
 * keyed on `lang`: a cover is an image and an id, with nothing localised to split the cache over.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';

import {
  deletePlanCoversId,
  getPlanCovers,
  postPlanCovers,
} from '~/shared/api/generated/endpoints/plan-cover/plan-cover';

import { planCategoryToApi, type PlanCategoryKey } from '../model/planCategory';

/** Every cover the trainer may pick for a plan in this category. */
export function planCoversQueryOptions(category: PlanCategoryKey) {
  const apiCategory = planCategoryToApi(category);
  return queryOptions({
    queryKey: ['plan-covers', apiCategory] as const,
    queryFn: ({ signal }) => getPlanCovers({ category: apiCategory }, undefined, signal),
  });
}

/**
 * The trainer's OWN covers — uploading one, removing one, and the invalidation both end with.
 *
 * ── Why the writes live beside the read ──────────────────────────────────────────────────────
 * They are the same pool. `GET /plan-covers` answers with `ownCount` and `ownLimit` alongside the
 * covers, so a write that did not invalidate this exact key would leave the counter and the row
 * disagreeing about what the trainer has. Keeping all three in one module is what makes that
 * impossible to forget — there is no repository layer over TanStack Query here, by house rule, so
 * `api/` is where a slice's requests live.
 *
 * The ceiling is the SERVER's (`PlanCoverProcessor.MaxOwnedCovers`), arriving as `ownLimit`. This
 * app never writes 10 down: the phone does not either, and a constant copied here would be a second
 * source of truth for a number only the backend can enforce.
 */

/** Add a picture to the trainer's own pool. The file is already encoded — see `shared/media`. */
export function uploadPlanCover(file: File) {
  return postPlanCovers({ file });
}

/**
 * Remove one of the trainer's own covers.
 *
 * The backend refuses a curated cover and another trainer's with a 404, and re-points any plan that
 * used this one at a curated cover of its category — so a plan never ends up with no picture.
 */
export function deletePlanCover(id: number) {
  return deletePlanCoversId(id);
}

/**
 * Drop EVERY category's cached list, not just the one on screen.
 *
 * An own upload carries a flat key server-side and therefore fits every category, so it appears in
 * all six lists at once. Invalidating only the current category left the other five holding a pool
 * that no longer exists — which is exactly what the phone's pool hook avoids the same way.
 */
export function invalidatePlanCovers(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ['plan-covers'] });
}
